import { useMemo } from 'react';
import type { FilterFieldDef } from '@components/ui/FilterMenu/types';
import { useGetPayerStatsQuery } from '@features/patient-coverages';
import { useRequirementCatalog } from '@features/patient-requirements';
import { useCan, useSubjectLanguageVariants } from '@features/user';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import { COVERAGE_OPTIONS, LIST_FIELDS } from '../utils/patientListQuery';

/**
 * The filter bar's fields.
 *
 * The requirement field appears only behind the same two checks as the rail:
 * the patient-requirements gate and `manage_billing`. It lists active
 * definitions that have finished evaluating; a definition still `checking` has
 * no settled population to filter to.
 */
export function usePatientListFilterFields(): {
  fields: FilterFieldDef[];
  canFilterRequirements: boolean;
} {
  const { singularCap } = useSubjectLanguageVariants();
  const requirementsEnabled = useFeatureGate(FeatureGates.PATIENT_REQUIREMENTS);
  const canManageBilling = useCan('manage_billing');
  const canFilterRequirements = requirementsEnabled && canManageBilling;
  const { data: payerStats, isLoading: payersLoading } = useGetPayerStatsQuery();
  const { definitions, isLoading: requirementsLoading } = useRequirementCatalog({
    skip: !canFilterRequirements,
  });

  const fields = useMemo<FilterFieldDef[]>(() => {
    const requirementOptions = definitions
      .filter(definition => definition.active && definition.materialization_status !== 'checking')
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(definition => ({
        value: definition.patient_requirement_definition_id,
        label: definition.label,
      }));
    return [
      {
        id: LIST_FIELDS.status,
        label: `${singularCap} status`,
        type: 'boolean',
        trueLabel: 'Active',
        falseLabel: 'Inactive',
      },
      {
        id: LIST_FIELDS.payers,
        label: 'Payer',
        type: 'multiSelect',
        loading: payersLoading,
        searchPlaceholder: 'Search payers',
        options: (payerStats?.payers ?? []).map(payer => ({
          value: payer.payer_uuid,
          label: payer.payer_name,
        })),
      },
      {
        id: LIST_FIELDS.coverage,
        label: 'Coverage',
        type: 'multiSelect',
        options: COVERAGE_OPTIONS,
      },
      ...(canFilterRequirements
        ? [
            {
              id: LIST_FIELDS.missing,
              label: 'Missing requirement',
              type: 'multiSelect' as const,
              loading: requirementsLoading,
              options: requirementOptions,
            },
          ]
        : []),
    ];
  }, [
    canFilterRequirements,
    definitions,
    payerStats,
    payersLoading,
    requirementsLoading,
    singularCap,
  ]);

  return { fields, canFilterRequirements };
}
